import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
} from '@backstage/core-components';
import {
  CatalogIcon,
  DocsIcon,
  GAuthIcon,
  MegatronIntegrationIcon,
  TemplatesIcon,
  UpdatesIcon,
  XelerateLogoIconMono,
} from '@xlrt/components';
import {
  RiFolderSharedLine,
  RiGithubFill,
  RiHome4Line,
  RiMenuLine,
  RiPuzzleLine,
  RiToolsLine,
} from '@remixicon/react';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { RequireFeatureFlag } from '../RequireFeatureFlag';
import { UnReadAnnoucement } from '@internal/backstage-plugin-announcements';
import { ExpertButton } from '@internal/backstage-plugin-mcp-chat';
import { useLocation } from 'react-router-dom';
import { useUserProfile } from '@backstage/plugin-user-settings';
import { usePermission } from '@backstage/plugin-permission-react';
import { mcpChatAccessPermission } from '@internal/plugin-permissions-common';
import styles from './Root.module.css';

export const Root = () => {
  const { pathname } = useLocation();
  const { backstageIdentity } = useUserProfile();
  const isAdmin =
    backstageIdentity?.ownershipEntityRefs.includes('group:default/admins') ??
    false;
  // Members of aiusers and admins, as the custom permission policy decides
  const { allowed: canUseXpert } = usePermission({
    permission: mcpChatAccessPermission,
  });
  const showExpertButton = !pathname.startsWith('/xpert');

  return (
    <>
      <Sidebar>
        <SidebarGroup label="Menu" icon={<RiMenuLine />}>
          <SidebarItem icon={() => <RiHome4Line />} to="/" text="Home" />

          <RequireFeatureFlag flag="enable-my-entities" errorPage={<></>}>
            <SidebarItem
              icon={() => <RiFolderSharedLine />}
              to="/my-assets"
              text="My Assets"
            />
          </RequireFeatureFlag>

          <SidebarItem
            icon={() => <CatalogIcon />}
            to="/catalog"
            text="Catalog"
          />
          <SidebarItem
            icon={() => <RiPuzzleLine />}
            to="/apis?filters[kind]=api"
            text="APIs"
          />
          <SidebarItem icon={() => <DocsIcon />} to="/docs" text="Docs" />
          <SidebarItem
            icon={() => <TemplatesIcon />}
            to="/create/templates"
            text="Templates"
          />

          <SidebarItem
            icon={() => <UpdatesIcon lightMode={false} />}
            to="/announcements"
            text="Updates"
          >
            <UnReadAnnoucement />
          </SidebarItem>

          <SidebarDivider />

          <RequireFeatureFlag
            flag="enable-github-landing-page"
            errorPage={<></>}
          >
            <SidebarItem
              icon={() => <RiGithubFill />}
              to="/github"
              text="GitHub"
            />
          </RequireFeatureFlag>

          <SidebarItem
            className={styles.megatronItem}
            icon={() => <MegatronIntegrationIcon />}
            to="/megatron/landingpage"
            text="Megatron"
          />

          <RequireFeatureFlag flag="gauth-plugin" errorPage={<></>}>
            <SidebarItem icon={() => <GAuthIcon />} to="/gauth" text="gAuth" />
          </RequireFeatureFlag>

          <SidebarDivider />

          {isAdmin && (
            <SidebarItem
              icon={() => <RiToolsLine />}
              to="/devtools"
              text="DevTools"
            />
          )}

          <SidebarItem
            icon={() => <XelerateLogoIconMono />}
            to="/about"
            text="About"
          />
        </SidebarGroup>
      </Sidebar>
      {canUseXpert && showExpertButton && (
        <RequireFeatureFlag flag="enable-ai-chat" errorPage={<></>}>
          <ExpertButton />
        </RequireFeatureFlag>
      )}
    </>
  );
};

const SidebarContent = NavContentBlueprint.make({
  name: 'xelerate-nav-content',
  params: { component: Root },
}) as any;

export const navModule = createFrontendModule({
  pluginId: 'app',
  extensions: [SidebarContent],
});
